/**
 * AuthContext — thin compatibility shim over Clerk's auth hooks.
 *
 * Keeps the same public API (useAuth, useHasRole) so all consuming
 * components work unchanged after the Supabase → Clerk migration.
 *
 * Role / status are authoritative in our Supabase user_profiles table;
 * we fetch them from /api/v2/auth/me after each login.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {
  useUser,
  useAuth as useClerkAuth,
  useClerk,
} from '@clerk/clerk-react';
import { registerTokenGetter } from '../lib/auth-bridge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

// ─── Types (unchanged public interface) ──────────────────────────────────────

export type AppRole = 'admin' | 'inspector' | 'viewer';

export interface UserProfile {
  id: string;
  clerk_id?: string;
  email: string;
  full_name: string;
  organisation?: string;
  role: AppRole;
  status: 'active' | 'pending' | 'suspended';
  license_no?: string;
  created_at?: string;
  last_login_at?: string;
}

interface AuthContextValue {
  user:    ReturnType<typeof useUser>['user'] | null;
  profile: UserProfile | null;
  role:    AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded }  = useUser();
  const { getToken }        = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();

  const [profile, setProfile]           = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /** Fetch full profile from our Workers API (role/status live in Supabase) */
  const fetchProfile = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_URL}/api/v2/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setProfile((json as { data: UserProfile }).data);
        return;
      }
    } catch {
      // fall through to fallback
    }

    // Webhook may not have fired yet — use Clerk user data as baseline
    if (user) {
      setProfile({
        id:        user.id,
        clerk_id:  user.id,
        email:     user.primaryEmailAddress?.emailAddress ?? '',
        full_name: user.fullName ?? user.firstName ?? '',
        role:      'viewer',
        status:    'active',
      });
    }
  }, [user]);

  // ── Sync token to localStorage + load profile on login ───────────────────

  // Register a fresh-token getter for module-level API helpers (apiFetch /
  // fetchJson). Without this they'd read a stale localStorage token. Clerk
  // session tokens expire in ~60 s, so calling getToken() right before each
  // request is the right pattern. We re-register whenever auth state changes.
  useEffect(() => {
    if (!user) {
      registerTokenGetter(null);
      return;
    }
    registerTokenGetter(async () => {
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
    return () => registerTokenGetter(null);
  }, [user, getToken]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      localStorage.removeItem('clerk_token');
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    // FORCE a fresh JWT on every mount with skipCache=true. Without this,
    // a JWT minted before the user was promoted (e.g. publicMetadata.role
    // empty at sign-in time) sticks around — backend reads role from JWT,
    // sees nothing, falls through to viewer, and inspector-gated endpoints
    // return 401. Refreshing here pulls the latest publicMetadata from
    // Clerk's server in one round-trip; no sign-out required.
    getToken({ skipCache: true } as { skipCache: boolean }).then(async (token: string | null) => {
      if (token) {
        localStorage.setItem('clerk_token', token);
        await fetchProfile(token);
      }
      setProfileLoading(false);
    });
  }, [isLoaded, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep token fresh every 4 minutes (tokens expire after 60 min) ─────────

  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      // skipCache so role/metadata changes propagate within ~4 min
      getToken({ skipCache: true } as { skipCache: boolean }).then((token: string | null) => {
        if (token) localStorage.setItem('clerk_token', token);
      });
    }, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, [user, getToken]);

  // ── Public API ────────────────────────────────────────────────────────────

  const signOut = async () => {
    localStorage.removeItem('clerk_token');
    setProfile(null);
    await clerkSignOut();
  };

  const refreshProfile = async () => {
    const token = await getToken();
    if (token) {
      localStorage.setItem('clerk_token', token);
      await fetchProfile(token);
    }
  };

  // Read role from Clerk's user.publicMetadata DIRECTLY — this reflects the
  // CURRENT state in Clerk (server-synced via useUser()), so role changes
  // (e.g. promotion to admin) take effect without sign-out. The Supabase
  // profile.role is the fallback when the JWT didn't carry one.
  const clerkRole = (user?.publicMetadata as { role?: AppRole } | undefined)?.role;
  const effectiveRole: AppRole | null =
    (clerkRole === 'admin' || clerkRole === 'inspector' || clerkRole === 'viewer')
      ? clerkRole
      : profile?.role ?? null;

  const value: AuthContextValue = {
    user:    user ?? null,
    profile,
    role:    effectiveRole,
    loading: !isLoaded || profileLoading,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Static signed-out provider for when Clerk isn't configured (placeholder /
 * missing publishable key). Public routes render normally; protected routes
 * see a signed-out user and redirect to /login, which explains the setup gap.
 */
export function NoAuthProvider({ children }: { children: React.ReactNode }) {
  const value: AuthContextValue = {
    user: null,
    profile: null,
    role: null,
    loading: false,
    signOut: async () => {},
    refreshProfile: async () => {},
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Returns true if the current user has at least one of the given roles */
export function useHasRole(...roles: AppRole[]): boolean {
  const { role } = useAuth();
  return role !== null && roles.includes(role);
}
